<template>
  <figure class="h-full flex flex-col rounded-2xl bg-white shadow-sm p-8 text-sm/6">
    <div class="mb-2">
      <MoleculeRatingStars :rating="review.OverallRating" />
    </div>

    <NuxtLink
      v-if="rvUrl"
      :to="rvUrl"
      target="_blank"
      class="text-base font-semibold hover:underline truncate text-secondary"
    >
      {{ rvLabel }}
    </NuxtLink>

    <span
      v-else
      class="text-base font-medium truncate text-gray-500 italic"
    >
      {{ t('unpublishedRv') }}
    </span>

    <blockquote class="flex-grow text-pretty mt-4 line-clamp-3">
      {{ review.Review }}
    </blockquote>

    <figcaption class="mt-6 flex items-center gap-x-4">
      <MoleculePersona
        :avatar="{ text: formatNameAsInitials(review.CreatedByFirstName, review.CreatedByLastName), src: review.CreatedByAvatar }"
        :name="formatNameAsFullName(review.CreatedByFirstName, review.CreatedByLastName)"
        :sub-name="formatAsMonthYear(review.CreatedAt)"
        size="sm"
      />
    </figcaption>
  </figure>
</template>

<script setup lang="ts">
import type { DestinationTripReview } from '~/types'
import { RV_MANUFACTURERS_OTHER } from '~/constants/manufacturers'

const props = defineProps<{
  review: DestinationTripReview
}>()

const { locale, t, te } = useI18n()
const localePath = useLocalePath()
const { formatAsMonthYear } = useDates()

const rvUrl = computed(() => {
  if (!props.review.RvInfo?.AliasName) {
    return null
  }

  return localePath(generateRvPath(props.review.RvInfo?.AliasName))
})

const rvLabel = computed(() => {
  const year = props.review.RvInfo?.Year
  const make = props.review.RvInfo?.Make !== RV_MANUFACTURERS_OTHER ? props.review.RvInfo?.Make : undefined
  const rvType = props.review.RvInfo?.RVType

  const type = te(`rvType.${rvType}`) ? t(`rvType.${rvType}`) : rvType

  const label = locale.value === 'fr' ? [type, make, year] : [year, make, type]

  return label.filter(Boolean).join(' ')
})
</script>

<i18n lang="json">
  {
    "en": {
      "unpublishedRv": "Unpublished RV"
    },
    "fr": {
      "unpublishedRv": "VR non publié"
    }
  }
</i18n>
